import React from 'react';
import { Card, Row, Col, Statistic, Progress } from 'antd';
import { motion } from 'framer-motion';
import { 
  ClockCircleOutlined, 
  CheckCircleOutlined, 
  DollarCircleOutlined,
  GlobalOutlined,
  TrophyOutlined,
  RiseOutlined
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DataCharts: React.FC = () => {
  // 时效统计数据
  const timeEfficiencyData = {
    labels: ['7天', '8天', '9天', '10天+'],
    datasets: [
      {
        label: '签收比例 (%)',
        data: [45, 35, 15, 5],
        backgroundColor: ['#52c41a', '#1890ff', '#faad14', '#f5222d'],
        borderWidth: 0,
      },
    ],
  };

  // 月度趋势数据
  const monthlyTrendData = {
    labels: ['1月', '2月', '3月', '4月', '5月', '6月'],
    datasets: [
      {
        label: '清关成功率 (%)',
        data: [94.5, 95.2, 96.1, 95.8, 96.5, 97.2],
        borderColor: '#52c41a',
        backgroundColor: 'rgba(82, 196, 26, 0.1)',
        tension: 0.4,
      },
      {
        label: '准时达成率 (%)',
        data: [92.1, 93.5, 94.2, 93.8, 95.1, 95.7],
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24, 144, 255, 0.1)',
        tension: 0.4,
      },
    ],
  };

  // 成本对比数据
  const costComparisonData = {
    labels: ['318空派', 'SN355', 'SNA57', 'SN392'],
    datasets: [
      {
        label: '美西价格(¥/kg)',
        data: [45.0, 40.0, 47.0, 46.0],
        backgroundColor: ['#52c41a', '#1890ff', '#faad14', '#722ed1'],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  };

  return (
    <div>
      {/* 核心指标卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={12} sm={8} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card bordered={false} style={{ textAlign: 'center' }}>
              <Statistic
                title="服务经验"
                value={10}
                suffix="年+"
                valueStyle={{ color: '#1890ff' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </motion.div>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Card bordered={false} style={{ textAlign: 'center' }}>
              <Statistic
                title="年交易额"
                value={10}
                suffix="亿+"
                valueStyle={{ color: '#52c41a' }}
                prefix={<DollarCircleOutlined />}
              />
            </Card>
          </motion.div>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card bordered={false} style={{ textAlign: 'center' }}>
              <Statistic
                title="服务国家地区"
                value={200}
                suffix="+"
                valueStyle={{ color: '#faad14' }}
                prefix={<GlobalOutlined />}
              />
            </Card>
          </motion.div>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Card bordered={false} style={{ textAlign: 'center' }}>
              <Statistic
                title="服务企业"
                value={100000}
                suffix="+"
                valueStyle={{ color: '#722ed1' }}
                prefix={<TrophyOutlined />}
              />
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* 图表展示 */}
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card title="318空派时效分布" bordered={false}>
              <div style={{ height: '300px' }}>
                <Doughnut data={timeEfficiencyData} options={doughnutOptions} />
              </div>
            </Card>
          </motion.div>
        </Col>
        
        <Col xs={24} md={12}>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card title="服务质量趋势" bordered={false}>
              <div style={{ height: '300px' }}>
                <Line data={monthlyTrendData} options={chartOptions} />
              </div>
            </Card>
          </motion.div>
        </Col>
        
        <Col xs={24} md={12}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card title="专线竞品对比" bordered={false}>
              <div style={{ height: '300px' }}>
                <Bar data={costComparisonData} options={chartOptions} />
              </div>
            </Card>
          </motion.div>
        </Col>

        <Col xs={24} md={12}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Card title="服务亮点" bordered={false}>
              <div style={{ padding: '20px 0' }}>
                <Row gutter={[16, 24]}>
                  <Col span={24}>
                    <div style={{ marginBottom: '16px' }}>
                      <TrophyOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                      直飞航班占比
                      <Progress percent={85} strokeColor="#faad14" style={{ marginTop: '8px' }} />
                    </div>
                  </Col>
                  <Col span={24}>
                    <div style={{ marginBottom: '16px' }}>
                      <RiseOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                      预申报成功率
                      <Progress percent={94} strokeColor="#52c41a" style={{ marginTop: '8px' }} />
                    </div>
                  </Col>
                  <Col span={24}>
                    <div style={{ marginBottom: '16px' }}>
                      <CheckCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      客户满意度
                      <Progress percent={98} strokeColor="#1890ff" style={{ marginTop: '8px' }} />
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>
          </motion.div>
        </Col>
      </Row>
    </div>
  );
};

export default DataCharts;
